import { createEmitter } from "../Utilities/events";
import { createPlugin } from "./createPlugin";
import { PLUGIN_FORWARDED_EVENTS } from "./pluginForwardedEvents";

/**
 * Hosts plugins without modifying the playback core.
 * Sprint 6A: forwards the full PlayerEvent set onto plugin.onEvent + bus.
 *
 * @param {object} engine
 * @param {object} config
 * @param {object} [extras] { tracks, getShell, getState }
 * @param {object} [options] { forwardEvents: string[] }
 */
export function createPluginHost(engine, config, extras, options) {
  extras = extras || {};
  options = options || {};
  var plugins = [];
  var bus = createEmitter();
  var ready = false;
  var engineUnsubs = [];
  var forwardList = options.forwardEvents || PLUGIN_FORWARDED_EVENTS;

  /**
   * Stable context identity across onRegister → onReady → onEvent → onDestroy.
   * Plugin private bags (ctx._resume, ctx._cwPlugin, …) must survive lifecycle
   * calls. Returning a fresh object each time dropped that state and crashed
   * resumePlaybackPlugin after cinematic remount (when resume is first registered).
   */
  var ctx = {
    engine: engine,
    config: config,
    bus: bus,
    eventBus: engine && engine.getEventBus ? engine.getEventBus() : bus,
    tracks: extras.tracks || null,
    shell: null,
    getState: function () {
      if (typeof extras.getState === "function") {
        return extras.getState();
      }
      return engine.getState();
    },
    getCapabilities: function () {
      if (engine && typeof engine.getCapabilities === "function") {
        return engine.getCapabilities();
      }
      return null;
    },
    getLifecyclePhase: function () {
      if (engine && typeof engine.getLifecyclePhase === "function") {
        return engine.getLifecyclePhase();
      }
      return null;
    },
  };

  function context() {
    ctx.engine = engine;
    ctx.config = config;
    ctx.bus = bus;
    ctx.eventBus =
      engine && engine.getEventBus ? engine.getEventBus() : bus;
    ctx.tracks = extras.tracks || null;
    ctx.shell =
      typeof extras.getShell === "function"
        ? extras.getShell()
        : extras.shell || null;
    return ctx;
  }

  /**
   * @param {object|function} pluginOrFactory
   */
  function register(pluginOrFactory) {
    var plugin =
      typeof pluginOrFactory === "function"
        ? pluginOrFactory(createPlugin)
        : pluginOrFactory;
    if (!plugin || !plugin.name) {
      throw new Error("Invalid plugin");
    }
    // Replace existing plugin with same name
    for (var i = 0; i < plugins.length; i++) {
      if (plugins[i].name === plugin.name) {
        destroyOne(plugins[i]);
        break;
      }
    }
    plugins.push(plugin);
    plugin.onRegister(context());
    if (ready && plugin.onReady) {
      try {
        plugin.onReady(context());
      } catch (e) {
        if (typeof console !== "undefined" && console.error) {
          console.error("[PluginHost] onReady failed:", plugin.name, e);
        }
      }
    }
    return function unregister() {
      destroyOne(plugin);
    };
  }

  function registerMany(list) {
    (list || []).forEach(register);
  }

  function unregister(name) {
    var p = get(name);
    if (!p) return false;
    destroyOne(p);
    return true;
  }

  function notifyReady() {
    ready = true;
    var shared = context();
    plugins.forEach(function (p) {
      if (p.onReady) {
        try {
          p.onReady(shared);
        } catch (e) {
          if (typeof console !== "undefined" && console.error) {
            console.error("[PluginHost] onReady failed:", p.name, e);
          }
        }
      }
    });
  }

  /** Allow host to refresh config reference after prop changes */
  function updateConfig(next) {
    config = next || config;
  }

  function destroyOne(plugin) {
    try {
      if (plugin.onDestroy) {
        plugin.onDestroy(context());
      }
    } catch (e) {
      /* isolate plugin errors */
    }
    plugins = plugins.filter(function (p) {
      return p !== plugin;
    });
  }

  function destroy() {
    engineUnsubs.forEach(function (u) {
      if (typeof u === "function") u();
    });
    engineUnsubs = [];
    plugins.slice().forEach(destroyOne);
    bus.removeAllListeners();
    plugins = [];
  }

  function list() {
    return plugins.map(function (p) {
      return { name: p.name, version: p.version };
    });
  }

  function get(name) {
    for (var i = 0; i < plugins.length; i++) {
      if (plugins[i].name === name) {
        return plugins[i];
      }
    }
    return null;
  }

  function forward(eventName) {
    if (!engine || typeof engine.on !== "function") return;
    var unsub = engine.on(eventName, function (payload) {
      plugins.forEach(function (p) {
        if (typeof p.onEvent === "function") {
          p.onEvent(eventName, payload, context());
        }
      });
      bus.emit(eventName, payload);
    });
    if (typeof unsub === "function") {
      engineUnsubs.push(unsub);
    }
  }

  // Deduplicate forward list
  var seen = Object.create(null);
  forwardList.forEach(function (name) {
    if (!name || seen[name]) return;
    seen[name] = true;
    forward(name);
  });

  return {
    register: register,
    registerMany: registerMany,
    unregister: unregister,
    notifyReady: notifyReady,
    updateConfig: updateConfig,
    destroy: destroy,
    list: list,
    get: get,
    bus: bus,
  };
}

export default createPluginHost;
